import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getAppointmentWithBalance } from 'api/Appointment';

import { inRn } from 'lib/helpers/utility';
import { useReceipt } from 'lib/hooks/queries/billing/useReceipt';
import { RECEIPT_CHANNELS, isChannelAvailable } from 'lib/helpers/receiptDelivery';

import Icon from 'components/shared/Icon/Icon';
import Loader from 'components/shared/Loader/Loader';
import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import Typography from 'components/shared/Typography/Typography';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import { STATUS_CONFIG } from '../PayFromKioskModal/components/ReaderStatus';

import DocumentPicker from './components/DocumentPicker';
import DocumentDelivery from './components/DocumentDelivery';
import { useDocumentDelivery } from './lib/useDocumentDelivery';
import { useDocumentSelection } from './lib/useDocumentSelection';
import MobilePaymentSummary from './components/MobilePaymentSummary';
import DocumentPreviewModals from './components/DocumentPreviewModals';
import { DOCUMENTS, DOCUMENT_IDS, receiptDocumentName } from './lib/constants';

const CheckinSuccess = ({ transactionId, token, patient }) => {
  const navigate = useNavigate();
  const [previewId, setPreviewId] = useState(null);

  const { values } = usePaymentContext();
  const appointmentId = values?.appointmentId;
  const invoiceId = values?.invoices?.[0]?.id;

  const {
    data: appointmentData,
    isInitialLoading: isAppointmentLoading,
    isError: isAppointmentError,
    refetch: refetchAppointment
  } = useQuery({
    queryKey: ['get_balance', appointmentId],
    queryFn: async () => {
      const response = await getAppointmentWithBalance(navigate, { appointmentId });
      if (response?.code !== 0) throw new Error(response?.error || 'Appointment request failed');
      return response;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!appointmentId
  });
  const appointment = appointmentData?.appointment;
  const deliveryChannels = appointmentData?.deliveryChannels;
  const canDeliver =
    isChannelAvailable(deliveryChannels, RECEIPT_CHANNELS.EMAIL) ||
    isChannelAvailable(deliveryChannels, RECEIPT_CHANNELS.SMS);

  const {
    data: receiptData,
    isInitialLoading: isReceiptLoading,
    isError: isReceiptError,
    refetch: refetchReceipt
  } = useReceipt({
    name: 'checkin_success_receipt',
    params: { transactionId, appointmentId, token },
    dependencies: [transactionId, appointmentId, token],
    options: { enabled: !!transactionId || !!appointmentId }
  });
  const transaction = receiptData?.receipt?.transaction;
  const cardLabel = transaction?.pan || null;

  const amount = inRn(transaction?.amount_paid_self ?? values?.amount);
  const surchargeAmount = inRn(
    transaction ? transaction?.surcharge_amount : values?.surchargeAmount
  );
  const gratuityAmount = inRn(transaction ? transaction?.gratuity : values?.gratuity);

  const resolvedTransactionId = transactionId || transaction?.id;

  // Without a resolved transaction there is nothing to deliver a receipt for, so it drops out of
  // the list rather than sending a request with a missing id.
  const documents = useMemo(
    () =>
      DOCUMENTS.filter(
        (document) => resolvedTransactionId || document.id !== DOCUMENT_IDS.RECEIPT
      ).map((document) =>
        document.id === DOCUMENT_IDS.RECEIPT
          ? { ...document, name: receiptDocumentName(transaction) }
          : document
      ),
    [resolvedTransactionId, transaction]
  );

  const selection = useDocumentSelection(documents);
  const { busy, send } = useDocumentDelivery({
    selectedIds: selection.selectedIds,
    appointmentId,
    transactionId: resolvedTransactionId,
    invoiceId,
    token
  });

  const summaryProps = {
    amount,
    surchargeAmount,
    gratuityAmount,
    cardLabel,
    transactionId: resolvedTransactionId
  };

  const isDocumentsLoading = isAppointmentLoading || isReceiptLoading;

  const retryDocuments = () => {
    refetchAppointment();
    if (isReceiptError) refetchReceipt();
  };

  const renderDocuments = () => {
    if (isDocumentsLoading)
      return (
        <div className="flex w-full items-center justify-center py-8">
          <Loader color="primary" size={32} />
        </div>
      );

    if (isAppointmentError)
      return (
        <div className="flex w-full flex-col items-center gap-3 py-8">
          <Typography variant="p" size="small" color="neutral-700" className="text-center">
            Your documents couldn’t be loaded. Your payment was still processed successfully.
          </Typography>
          <Button
            text="Try again"
            size="small"
            color="neutral"
            style="outlined"
            onClick={retryDocuments}
          />
        </div>
      );

    return (
      <>
        <DocumentPicker documents={documents} {...selection} onPreview={setPreviewId} />
        {canDeliver && (
          <>
            <Divider className="shrink-0" />
            <DocumentDelivery
              patient={appointment?.patient ?? patient}
              channels={deliveryChannels}
              isDisabled={!selection.hasSelection}
              selectedIds={selection.selectedIds}
              areAllSelected={selection.areAllSelected}
              busy={busy}
              onSend={send}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div className="flex h-full items-stretch justify-between">
      <div className="flex min-h-0 flex-1 flex-col items-center gap-6 overflow-y-auto px-11 py-8 xs:px-4">
        <div className="flex w-full min-w-0 max-w-[600px] flex-col items-center gap-4 motion-safe:animate-enter-up">
          <Icon icon="illustration-confirmed" />
          <div className="flex max-w-[520px] flex-col items-center gap-1">
            <Typography variant="p" weight="medium">
              {STATUS_CONFIG.success.title}
            </Typography>
            <Typography variant="p" size="small" className="text-center">
              {STATUS_CONFIG.success.subtitle}
            </Typography>
          </div>
        </div>
        <div className="w-full max-w-[600px] motion-safe:animate-enter-up-2">
          <MobilePaymentSummary {...summaryProps} />
        </div>
        <div className="flex w-full flex-col items-center gap-6 motion-safe:animate-enter-up-3">
          {renderDocuments()}
        </div>
      </div>
      <DocumentPreviewModals
        previewId={previewId}
        onClose={() => setPreviewId(null)}
        appointment={appointment}
        transactionId={resolvedTransactionId}
        surchargeAmount={surchargeAmount}
        token={token}
        channels={deliveryChannels}
      />
    </div>
  );
};

export default CheckinSuccess;
